import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import SimCardOutlinedIcon from '@mui/icons-material/SimCardOutlined';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import { toast } from 'react-toastify';
import { tokens } from '../../theme';
import api from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';
import Header from '../../components/Header';
import { Field, SectionCard } from './resaleDetails';
import { Gift } from 'lucide-react';
import CheckIcon from '@mui/icons-material/Check';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Button as CustomButton } from '../../components/Button';
import ConsultationValuesSection from './ConsultationValuesSection';

export const NumberInput = ({ value, onChange, min = 0, max }) => {
  const handleDecrease = () => {
    if (value > min) onChange(value - 1);
  };

  const handleIncrease = () => {
    if (!max || value < max) onChange(value + 1);
  };

  const handleInputChange = (e) => {
    const val = e.target.value.replace(/\D/g, '');
    onChange(val === '' ? 0 : Number(val));
  };

  const squareButtonSx = {
    width: 44,
    height: 44,
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    color: '#424242',
    '&:hover': { backgroundColor: '#f5f5f5' },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: 'fit-content',
      }}
    >
      <IconButton onClick={handleDecrease} sx={squareButtonSx}>
        <RemoveIcon />
      </IconButton>

      <TextField
        value={value}
        onChange={handleInputChange}
        variant="outlined"
        inputProps={{
          style: { textAlign: 'center', width: 44, fontWeight: 700 },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            height: 44,
            '& fieldset': { borderColor: '#e0e0e0' },
          },
          '& input': {
            textAlign: 'center',
            fontWeight: 700,
            color: '#424242',
          },
        }}
      />

      <IconButton onClick={handleIncrease} sx={squareButtonSx}>
        <AddIcon />
      </IconButton>
    </Box>
  );
};

const ResaleSettings = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [freeConsults, setFreeConsults] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [simulators, setSimulators] = useState([]);
  const [linkedSimulatorId, setLinkedSimulatorId] = useState('');
  const [savingSimulator, setSavingSimulator] = useState(false);

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      const { data: response } = await api.get(`/v1/resale/${id}`);
      setData(response);

      setLinkedSimulatorId(response?.company?.simulator_config_id ?? '');
    } catch (error) {
      console.error('Erro ao buscar detalhes da revenda', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchSimulators = useCallback(async () => {
    try {
      const { data: res } = await api.get('/v1/simulators');
      setSimulators(res?.data ?? []);
    } catch {
      toast.error('Erro ao carregar simuladores.');
    }
  }, []);

  useEffect(() => {
    fetchDetail();
    fetchSimulators();
  }, [fetchDetail, fetchSimulators]);

  const handleSimulatorChange = async (newSimulatorId) => {
    const previousId = linkedSimulatorId;
    const companyId = data?.company?.id;

    if (!companyId) return;

    setLinkedSimulatorId(newSimulatorId);
    setSavingSimulator(true);

    try {
      if (previousId) {
        await api.delete(`/v1/simulators/${previousId}/resales/${companyId}`);
      }
      if (newSimulatorId) {
        await api.post(`/v1/simulators/${newSimulatorId}/resales/${companyId}`);
      }
      toast.success('Simulador vinculado com sucesso!');
    } catch {
      toast.error('Erro ao atualizar vínculo do simulador.');
      setLinkedSimulatorId(previousId);
    } finally {
      setSavingSimulator(false);
    }
  };

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (freeConsults === 0) return;

    setSaving(true);

    try {
      await api.post('/v1/resale/balance', {
        company_id: company.id,
        amount: freeConsults,
        description: 'Ajuste manual via painel admin',
      });

      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <Backdrop open sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  const user = data?.user ?? {};
  const company = data?.company ?? {};

  return (
    <Box m="20px">
      <Button
        onClick={() => navigate(-1)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: colors.grey[300],
          textTransform: 'none',
          padding: 0,
          gap: 1,
          marginBottom: 3,
          '&:hover': { backgroundColor: 'transparent' },
        }}
      >
        <ArrowBackIcon />
        <Typography fontSize={14} fontWeight={400}>
          {t('resale.backToResales')}
        </Typography>
      </Button>

      <Header title={t('resale.resaleConfig')} subtitle={t('resale.resaleConfigSubtitle')} />

      <SectionCard
        icon={<BusinessIcon sx={{ fontSize: 18, color: '#757575' }} />}
        title={t('resale.resaleData')}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Field label={t('resale.userName')} value={user.name} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field label={t('forms.companyName')} value={company.name} />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard
        icon={<Gift size={18} color="#757575" strokeWidth={1.5} />}
        title={t('resale.freeConsults')}
        subtitle={t('resale.freeConsultsSubtitle')}
      >
        <Typography variant="h6" sx={{ fontSize: '12px', color: '#9e9e9e', mb: 1.5 }}>
          {t('resale.freeAvailable').toUpperCase()} <br />{' '}
          <span style={{ color: colors.grey[500], fontWeight: 700, fontSize: '14px' }}>
            {data.granted_consultations || 0}
          </span>
        </Typography>

        <Typography variant="h6" sx={{ fontSize: '12px', color: '#9e9e9e', mb: 1.5 }}>
          {t('resale.addFreeConsults').toUpperCase()} <br />{' '}
        </Typography>

        <NumberInput value={freeConsults} onChange={setFreeConsults} min={0} />

        <Typography sx={{ fontSize: '12px', color: '#9e9e9e', mt: 1.5 }}>
          {t('resale.freeSubtitle')}
        </Typography>
      </SectionCard>

      <ConsultationValuesSection resaleId={id} />

      <SectionCard
        icon={<SimCardOutlinedIcon sx={{ fontSize: 18, color: '#757575' }} />}
        title="Simulador vinculado"
      >
        <Typography sx={{ fontSize: '12px', color: '#9e9e9e', mb: 1.5 }}>
          Selecione qual simulador estará disponível para esta revenda.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, maxWidth: '320px' }}>
          <Select
            size="small"
            fullWidth
            displayEmpty
            value={linkedSimulatorId}
            onChange={(e) => handleSimulatorChange(e.target.value)}
            disabled={savingSimulator}
            sx={{ fontSize: '13px' }}
          >
            <MenuItem value="">
              <Typography sx={{ fontSize: '13px', color: '#9e9e9e', fontStyle: 'italic' }}>
                Nenhum
              </Typography>
            </MenuItem>
            {simulators.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
          {savingSimulator && <CircularProgress size={18} />}
        </Box>
      </SectionCard>
      <Box mt={3} display="flex" justifyContent="flex-end">
        <CustomButton fullWidth={false} onClick={handleSave} disabled={freeConsults === 0}>
          {saving ? (
            <CircularProgress size={20} />
          ) : (
            <Typography sx={{ fontSize: '13px' }}>
              {' '}
              <CheckIcon sx={{ mr: 1 }} />
              Salvar alterações
            </Typography>
          )}
        </CustomButton>
      </Box>
    </Box>
  );
};

export default ResaleSettings;
